import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";
import {
  getConsent,
  setConsent,
  acceptAll,
  rejectAll,
  onCookieSettingsOpen,
} from "@/lib/consent";

const CATEGORIES = [
  {
    key: "necessary",
    label: "Strictly Necessary",
    desc:
      "Required for the site to function: session, authentication, and security. These cannot be disabled.",
    locked: true,
  },
  {
    key: "analytics",
    label: "Analytics",
    desc:
      "Helps us understand which routes and pages travelers explore so we can improve the experience (e.g. Google Analytics).",
  },
  {
    key: "marketing",
    label: "Marketing",
    desc:
      "Used to measure ad performance and show relevant offers across Google and partner networks.",
  },
];

export default function CookieConsent() {
  const [banner, setBanner] = useState(false);
  const [panel, setPanel] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, analytics: false, marketing: false });

  // Initial mount — show banner if user has never decided.
  useEffect(() => {
    const existing = getConsent();
    if (!existing) {
      setBanner(true);
    } else {
      setPrefs({
        necessary: true,
        analytics: !!existing.analytics,
        marketing: !!existing.marketing,
      });
    }
  }, []);

  // Allow Footer link (or any code) to reopen preferences at any time.
  useEffect(
    () =>
      onCookieSettingsOpen(() => {
        const existing = getConsent();
        if (existing) {
          setPrefs({
            necessary: true,
            analytics: !!existing.analytics,
            marketing: !!existing.marketing,
          });
        }
        setBanner(false);
        setPanel(true);
      }),
    [],
  );

  const handleAcceptAll = () => {
    acceptAll();
    setBanner(false);
    setPanel(false);
  };

  const handleRejectAll = () => {
    rejectAll();
    setBanner(false);
    setPanel(false);
  };

  const handleSave = () => {
    setConsent({ analytics: !!prefs.analytics, marketing: !!prefs.marketing });
    setBanner(false);
    setPanel(false);
  };

  const togglePref = (key, value) => {
    if (key === "necessary") return;
    setPrefs((p) => ({ ...p, [key]: value }));
  };

  if (!banner && !panel) return null;

  return (
    <>
      {/* First-visit banner anchored bottom */}
      {banner && !panel && (
        <div
          role="dialog"
          aria-live="polite"
          aria-label="Cookie consent"
          data-testid="cookie-banner"
          className="fixed bottom-4 left-4 right-4 md:left-6 md:right-6 lg:left-auto lg:right-8 lg:bottom-8 lg:max-w-[460px] z-[60] bg-[#0F0F10]/95 backdrop-blur-xl border border-white/10 text-[#F7F5F0] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.7)]"
        >
          <div className="p-6 md:p-7">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-9 h-9 shrink-0 border border-[#C29B62]/40 text-[#C29B62] flex items-center justify-center rounded-full">
                <Cookie size={18} weight="duotone" />
              </div>
              <div>
                <p className="tcp-overline text-[#C29B62] mb-1">Your privacy</p>
                <h3 className="text-lg font-medium leading-tight">A note on cookies</h3>
              </div>
            </div>
            <p className="text-sm text-[#A09E9A] leading-relaxed mb-5">
              We use strictly necessary cookies to run the site and — with your permission —
              analytics &amp; marketing cookies to improve the experience and measure
              campaign performance. Read our{" "}
              <Link
                to="/cookies"
                className="text-[#C29B62] underline-offset-2 hover:underline"
                data-testid="cookie-banner-policy-link"
              >
                Cookie Policy
              </Link>
              .
            </p>

            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={handleRejectAll}
                data-testid="cookie-reject-all"
                className="flex-1 px-4 py-2.5 text-xs tracking-[0.16em] uppercase font-semibold border border-white/15 text-[#F7F5F0] hover:border-[#C29B62] hover:text-[#C29B62] transition-colors"
              >
                Reject all
              </button>
              <button
                type="button"
                onClick={() => setPanel(true)}
                data-testid="cookie-customize"
                className="flex-1 px-4 py-2.5 text-xs tracking-[0.16em] uppercase font-semibold border border-white/15 text-[#F7F5F0] hover:border-[#C29B62] hover:text-[#C29B62] transition-colors"
              >
                Customize
              </button>
              <button
                type="button"
                onClick={handleAcceptAll}
                data-testid="cookie-accept-all"
                className="flex-1 px-4 py-2.5 text-xs tracking-[0.16em] uppercase font-semibold bg-[#C29B62] hover:bg-[#D4AC75] text-[#0A0A0B] transition-colors"
              >
                Accept all
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Granular preferences modal */}
      {panel && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Cookie preferences"
          data-testid="cookie-preferences-modal"
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setPanel(false)}
          />
          <div className="relative w-full max-w-lg bg-[#0F0F10] border border-white/10 text-[#F7F5F0] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)] max-h-[90vh] flex flex-col">
            <div className="flex items-start justify-between gap-4 p-6 md:p-7 border-b border-white/10">
              <div>
                <p className="tcp-overline text-[#C29B62] mb-1">Manage cookies</p>
                <h3 className="text-xl font-medium leading-tight">Cookie preferences</h3>
                <p className="text-sm text-[#A09E9A] mt-2 leading-relaxed">
                  Choose which categories you allow. You can change this any time from the
                  footer.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPanel(false)}
                aria-label="Close preferences"
                data-testid="cookie-preferences-close"
                className="text-[#A09E9A] hover:text-[#F7F5F0] transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 md:p-7 overflow-y-auto space-y-5">
              {CATEGORIES.map((c) => (
                <div
                  key={c.key}
                  className="flex items-start justify-between gap-4 pb-5 border-b border-white/5 last:border-0 last:pb-0"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#F7F5F0] flex items-center gap-2">
                      {c.label}
                      {c.locked && (
                        <span className="text-[10px] tracking-[0.18em] uppercase text-[#C29B62]">
                          Always on
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-[#A09E9A] mt-1 leading-relaxed">{c.desc}</p>
                  </div>
                  <Switch
                    checked={!!prefs[c.key]}
                    disabled={c.locked}
                    onCheckedChange={(v) => togglePref(c.key, v)}
                    data-testid={`cookie-toggle-${c.key}`}
                    className="data-[state=checked]:bg-[#C29B62] data-[state=unchecked]:bg-white/15 shrink-0"
                  />
                </div>
              ))}
            </div>

            <div className="p-6 md:p-7 border-t border-white/10 flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={handleRejectAll}
                data-testid="cookie-prefs-reject-all"
                className="flex-1 px-4 py-2.5 text-xs tracking-[0.16em] uppercase font-semibold border border-white/15 text-[#F7F5F0] hover:border-[#C29B62] hover:text-[#C29B62] transition-colors"
              >
                Reject all
              </button>
              <button
                type="button"
                onClick={handleSave}
                data-testid="cookie-prefs-save"
                className="flex-1 px-4 py-2.5 text-xs tracking-[0.16em] uppercase font-semibold border border-white/15 text-[#F7F5F0] hover:border-[#C29B62] hover:text-[#C29B62] transition-colors"
              >
                Save preferences
              </button>
              <button
                type="button"
                onClick={handleAcceptAll}
                data-testid="cookie-prefs-accept-all"
                className="flex-1 px-4 py-2.5 text-xs tracking-[0.16em] uppercase font-semibold bg-[#C29B62] hover:bg-[#D4AC75] text-[#0A0A0B] transition-colors"
              >
                Accept all
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
